import updateObjectPath from '@niaratech/niara-js-commons/dist/updateObjectPath';
import dotProp from 'dot-prop';
import React, { useCallback, useMemo, useReducer, useState, forwardRef, useImperativeHandle } from 'react';
import { CSSProperties } from 'styled-components';
import type { AddRemoveFieldValidator, Context as FormContextValue, FieldErrors, FieldValidator, SetFieldEvent } from './FormContext';
import FormContext, { SetFieldListener } from './FormContext';
import * as S from './styles';
import { useImmutableFunction, useLocalOrPropState, useScrollTo } from '@niaratech/niara-react-components';
export type FormProps<V> = React.PropsWithChildren<{
  value?: V;
  setValue?: React.Dispatch<React.SetStateAction<V>>;
  disabled?: boolean;
  onSubmit?: {
    (value: V);
  };
  style?: CSSProperties;
  className?: string;
  /**
   * Não gera um form html
   */
  noHTMLForm?: boolean;
  formContextRef?: React.Ref<FormContextValue<V> | undefined>;
  /**
   * Ao fazer um scroll até o componente (ex: scroll até componente com erro de validação), qual o
   * parâmetro deltaY do useScrollTo. Padrão é -90, considerando um navbar sticky de 70px.
   */
  useScrollToDeltaY?: number;
  HTMLFormProps?: Omit<Partial<React.FormHTMLAttributes<HTMLFormElement>>, 'noValidate' | 'onSubmit'>;
  /**
   * Dê preferência para função que não muda a cada renderização
   */
  onSetField?: SetFieldListener;
  autoValidation?: boolean | 'FORCE';
}>;
const fieldValidationsReducer = (state: Array<FieldValidator> = [], action: FieldValidator) => {
  if (!action) return state;
  if (state.indexOf(action) >= 0) return state.filter(x => x !== action);else return [...state, action];
};
const htmlElementRefReducer = (state: Record<string, React.RefObject<HTMLElement>>, action: {
  type: 'ADD' | 'REMOVE';
  fieldPath: string;
  ref: React.RefObject<HTMLElement>;
}) => {
  if (action?.type == 'ADD') return {
    ...state,
    [action.fieldPath]: action.ref
  };
  if (action?.type == 'REMOVE') {
    const newState = {
      ...state
    };
    delete newState[action.fieldPath];
    return newState;
  }
};
type FormType = {
  <V>(props: FormProps<V> & React.RefAttributes<HTMLFormElement | undefined>): React.ReactElement | null;
};
const Form: FormType = React.forwardRef(function Form<V>(props: FormProps<V>, ref: React.Ref<HTMLFormElement | undefined>) {
  const {
    disabled,
    onSubmit,
    noHTMLForm,
    formContextRef,
    useScrollToDeltaY = -90,
    autoValidation
  } = props;
  const [_value, _setValue] = useLocalOrPropState(props.value, props.setValue);
  const valueRef = React.useRef(_value);
  valueRef.current = _value;
  const setValue: typeof _setValue = useImmutableFunction(fn => {
    const v = typeof fn === 'function' ? (fn as (v: V) => V)(valueRef.current) : fn;
    valueRef.current = v;
    _setValue(v);
  });
  const getValue = useImmutableFunction(() => valueRef.current);
  const [fieldValidators, _addRemoveFieldValidator] = useReducer(fieldValidationsReducer, []);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();
  const [[addSetFieldListener, removeSetFieldListener, setFieldListeners]] = React.useState<[FormContextValue<V>['addSetFieldListener'], FormContextValue<V>['removeSetFieldListener'], SetFieldListener[]]>(() => {
    const setFieldListeners = [];
    const addSetFieldListener = listener => {
      if (listener && setFieldListeners.indexOf(listener) == -1) {
        setFieldListeners.push(listener);
      }
    };
    const removeSetFieldListener = listener => {
      if (listener) {
        const index = setFieldListeners.indexOf(listener);
        if (index >= 0) {
          setFieldListeners.splice(index, 1);
        }
      }
    };
    return [addSetFieldListener, removeSetFieldListener, setFieldListeners];
  });
  React.useEffect(() => {
    if (props.onSetField) {
      addSetFieldListener(props.onSetField);
      return () => {
        removeSetFieldListener(props.onSetField);
      };
    }
  }, [addSetFieldListener, props.onSetField, removeSetFieldListener]);
  const addRemoveFieldValidator: AddRemoveFieldValidator = useImmutableFunction(p => {
    _addRemoveFieldValidator(p);
    return () => _addRemoveFieldValidator(p);
  });

  // armazena os refs de inputs a partir do fieldPath - setados pelo useFormInput
  const [htmlelementRefsByFieldPath, fieldPathRefsDispatch] = useReducer(htmlElementRefReducer, {});
  const removeHtmlElementRef = useImmutableFunction<FormContextValue<V>['removeHtmlElementRef']>((fieldPath, ref) => {
    fieldPathRefsDispatch({
      type: 'REMOVE',
      fieldPath,
      ref
    });
  });
  const addHtmlElementRef = useImmutableFunction<FormContextValue<V>['addHtmlElementRef']>((fieldPath, ref) => {
    fieldPathRefsDispatch({
      type: 'ADD',
      fieldPath,
      ref
    });
    return () => {
      fieldPathRefsDispatch({
        type: 'REMOVE',
        fieldPath,
        ref
      });
    };
  });

  //ref usada no form submit
  const inputRefsByFieldPathRef = React.useRef(htmlelementRefsByFieldPath);
  inputRefsByFieldPathRef.current = htmlelementRefsByFieldPath;
  const setField = useImmutableFunction(function <F>(fieldPath: string, setStateOrValue: React.SetStateAction<F>) {
    let _setFieldListenerEventData: [V, F];
    setValue?.(formValue => {
      const prevPathValue = dotProp.get(formValue, fieldPath) as F;
      const newPathValue = typeof setStateOrValue === 'function' ? (setStateOrValue as (v: F) => F)(prevPathValue) : setStateOrValue;
      if (prevPathValue === newPathValue) return formValue;else {
        const newFormValue = updateObjectPath(formValue, fieldPath, newPathValue, true);
        _setFieldListenerEventData = [newFormValue, newPathValue];
        return newFormValue;
      }
    });
    if (_setFieldListenerEventData) {
      setFieldListeners?.reduce<SetFieldEvent<V, F>>((event, listener) => {
        listener?.(event);
        return event;
      }, {
        fieldPath,
        value: _setFieldListenerEventData[1],
        formValue: _setFieldListenerEventData[0]
      });
    }
  });

  /**
   * usado para scrollar para o campo com erro quando o form é submetido
   */
  const scrollToComponent = useScrollTo(undefined, undefined, undefined, useScrollToDeltaY);
  const getField = useImmutableFunction(fieldPath => {
    return valueRef.current ? dotProp.get(valueRef.current, fieldPath) : undefined;
  });
  const _onFormSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(e => {
    e?.preventDefault();
    const form = e.currentTarget;
    //faz validações de fields
    let firstElementWithError: HTMLElement;
    const fieldErrors = fieldValidators?.reduce((acc, fieldValidation) => {
      for (const [fieldPath, validators] of Object.entries(fieldValidation)) {
        const validatorsArray = Array.isArray(validators) ? validators : [validators];
        validatorsArray.reduce((acc2, validator) => {
          const validationResult = validator(getField(fieldPath), valueRef.current);
          if (validationResult?.errors?.length > 0) {
            acc2[fieldPath] = validationResult?.errors;
            if (!firstElementWithError) {
              const ref: React.RefObject<HTMLElement> = inputRefsByFieldPathRef.current?.[fieldPath];
              if (ref && ref.current) {
                firstElementWithError = ref.current;
              }
            }
          }
          return acc2;
        }, acc);
      }
      return acc;
    }, {} as FieldErrors);
    setFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      // scroll até campo com erro
      if (firstElementWithError) {
        scrollToComponent(firstElementWithError);
      }
    } else if (form.checkValidity()) {
      onSubmit?.(valueRef.current);
    }
  }, [fieldValidators, getField, onSubmit, scrollToComponent]);
  const onFormSubmit = useImmutableFunction(_onFormSubmit);
  const context: FormContextValue<V> = useMemo(() => ({
    value: _value,
    setValue,
    getValue,
    disabled,
    setField,
    getField,
    addRemoveFieldValidator,
    fieldErrors,
    onSubmit: onFormSubmit,
    addHtmlElementRef,
    removeHtmlElementRef,
    addSetFieldListener,
    removeSetFieldListener,
    autoValidation
  }), [_value, setValue, getValue, disabled, setField, getField, addRemoveFieldValidator, fieldErrors, onFormSubmit, addHtmlElementRef, removeHtmlElementRef, addSetFieldListener, removeSetFieldListener, autoValidation]);
  useImperativeHandle(formContextRef, () => context, [context]);
  if (noHTMLForm) {
    return <FormContext.Provider value={context}>{props.children}</FormContext.Provider>;
  }
  return <FormContext.Provider value={context}>
      <S.Form {...props.HTMLFormProps} onSubmit={onFormSubmit} noValidate className={props.className ?? props.HTMLFormProps?.className} style={props.style ?? props.HTMLFormProps?.style} ref={ref}>
        {props.children}
      </S.Form>
    </FormContext.Provider>;
});
export default Form;